import { OfferChatMessage } from './types';

interface MessageTimelineItem {
    type: 'message';
    message: OfferChatMessage;
}

interface CollapsedSystemMessagesTimelineItem {
    type: 'collapsedSystemMessages';
    id: string;
    messages: OfferChatMessage[];
}

export type ActivityTimelineItem = MessageTimelineItem | CollapsedSystemMessagesTimelineItem;

export type TimelineSpacingItem =
    | {
          type: 'chat';
          senderKey: string;
      }
    | {
          type: 'system';
      }
    | {
          type: 'collapsedSystemMessages';
      };

export const buildActivityTimelineItems = (messages: OfferChatMessage[]): ActivityTimelineItem[] => {
    if (messages.length <= 2) {
        return messages.map((message) => ({ type: 'message', message }));
    }

    const firstMessage = messages[0];
    const lastMessage = messages[messages.length - 1];
    const collapsibleMessages = messages.slice(1, -1);
    const items: ActivityTimelineItem[] = [];
    let index = 0;

    while (index < collapsibleMessages.length) {
        const message = collapsibleMessages[index];

        if (!message.systemMessage) {
            items.push({ type: 'message', message });
            index += 1;
            continue;
        }

        const systemMessages: OfferChatMessage[] = [];

        while (collapsibleMessages[index]?.systemMessage) {
            systemMessages.push(collapsibleMessages[index]);
            index += 1;
        }

        if (systemMessages.length >= 3) {
            items.push({
                type: 'collapsedSystemMessages',
                id: `collapsed-${systemMessages[0].id}-${systemMessages.at(-1)?.id}`,
                messages: systemMessages,
            });
            continue;
        }

        systemMessages.forEach((systemMessage) => {
            items.push({ type: 'message', message: systemMessage });
        });
    }

    return [{ type: 'message', message: firstMessage }, ...items, { type: 'message', message: lastMessage }];
};

export const getMessageSenderKey = (message: OfferChatMessage): string => {
    if (message.user?.id) {
        return `user:${message.user.id}`;
    }

    if (message.customer?.id) {
        return `customer:${message.customer.id}`;
    }

    if (message.supplier?.id) {
        return `supplier:${message.supplier.id}`;
    }

    return message.senderType;
};

export const getTimelineSpacingItem = (item: ActivityTimelineItem): TimelineSpacingItem => {
    if (item.type === 'collapsedSystemMessages') {
        return { type: 'collapsedSystemMessages' };
    }

    if (item.message.systemMessage) {
        return { type: 'system' };
    }

    return {
        type: 'chat',
        senderKey: getMessageSenderKey(item.message),
    };
};

export const getTimelineItemSpacingBefore = (
    previousItem: TimelineSpacingItem | null,
    currentItem: TimelineSpacingItem
): number => {
    if (!previousItem) {
        return 0;
    }

    if (previousItem.type === 'chat' && currentItem.type === 'chat') {
        return previousItem.senderKey === currentItem.senderKey ? 8 : 16;
    }

    if (
        (previousItem.type === 'system' || previousItem.type === 'collapsedSystemMessages') &&
        (currentItem.type === 'system' || currentItem.type === 'collapsedSystemMessages')
    ) {
        return 0;
    }

    return 16;
};

export const buildCollapsedEventsLabel = (messages: OfferChatMessage[], previewLimit = 4): string => {
    const eventLabels = messages.slice(0, previewLimit).map((message) => message.message);
    const suffix = messages.length > previewLimit ? ',...' : '';

    return `${eventLabels.join(', ')}${suffix}`;
};
